import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ApartmentIcon from '@mui/icons-material/Apartment';
import type { MasterToolProjectResult } from '../../services/api';
import CompareSuperRatioChart from './CompareSuperRatioChart';

export const COMPARE_CART_MAX = 3;

const CYAN = '#22d3ee';

const microLabelSx = {
  fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: 'rgba(148, 197, 222, 0.75)',
};

interface CompareCartDialogProps {
  open: boolean;
  projects: MasterToolProjectResult[];
  onClose: () => void;
  onRemove: (projectId: string) => void;
  onClear: () => void;
  /** Outer edge of the Super Ratio scan ring in meters. */
  radiusMeters?: number;
  /** Inner edge of the Super Ratio scan ring in meters; 0 scans from the centre. */
  minRadiusMeters?: number;
  /**
   * Super Ratio access. Super Map and Super Ratio are separate grants, so a
   * SUPER-MAP user can reach this dialog without it — the comparison mode is
   * hidden until the authenticated session capability has loaded.
   */
  canUseSuperRatio?: boolean;
}

type CompareMode = 'details' | 'superRatio';

const MODES: Array<{ id: CompareMode; label: string }> = [
  { id: 'details', label: 'Project details' },
  { id: 'superRatio', label: 'Super Ratio' },
];

type CompareColumn = MasterToolProjectResult & { id: string };

type AttrRow = {
  key: string;
  label: string;
  getValue: (project: CompareColumn) => string;
};

type AttrSection = {
  id: string;
  title: string;
  rows: AttrRow[];
};

const formatDistrict = (district: string | null | undefined): string => {
  if (!district) return '—';
  return String(district).toUpperCase().startsWith('D')
    ? String(district).toUpperCase()
    : `D${district}`;
};

const formatCount = (value: number | string | null | undefined): string => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-SG').format(n);
};

const formatDate = (value: Date | string | null | undefined): string => {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(d.getTime())) {
    const asString = String(value).trim();
    return asString || '—';
  }
  return d.toLocaleDateString('en-SG', { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatWalkMins = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value} min`;
};

const SECTIONS: AttrSection[] = [
  {
    id: 'location',
    title: 'Location',
    rows: [
      { key: 'district', label: 'District', getValue: (p) => formatDistrict(p.district) },
      { key: 'region', label: 'Region', getValue: (p) => p.area?.trim() || '—' },
      { key: 'address', label: 'Address', getValue: (p) => p.address?.trim() || '—' },
      { key: 'mrt', label: 'Nearest MRT', getValue: (p) => p.nearestMrt?.trim() || '—' },
      { key: 'walk', label: 'Walk to MRT', getValue: (p) => formatWalkMins(p.walkingTimeToMrt) },
    ],
  },
  {
    id: 'project',
    title: 'Project Facts',
    rows: [
      { key: 'developer', label: 'Developer', getValue: (p) => p.developer?.trim() || '—' },
      { key: 'tenure', label: 'Tenure', getValue: (p) => p.tenure?.trim() || '—' },
      { key: 'top', label: 'TOP', getValue: (p) => (p.topDate != null ? String(p.topDate) : '—') },
      { key: 'completed', label: 'Completed', getValue: (p) => formatDate(p.completedDate) },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory',
    rows: [
      { key: 'totalUnits', label: 'Total Units', getValue: (p) => formatCount(p.totalUnits) },
      { key: 'soldUnits', label: 'Sold Units', getValue: (p) => formatCount(p.soldUnits) },
    ],
  },
];

const cellWrapSx = {
  overflow: 'hidden',
  whiteSpace: 'normal' as const,
  overflowWrap: 'anywhere' as const,
  wordBreak: 'break-word' as const,
};

const CompareCartDialog: React.FC<CompareCartDialogProps> = ({
  open,
  projects,
  onClose,
  onRemove,
  onClear,
  radiusMeters,
  minRadiusMeters,
  canUseSuperRatio = false,
}) => {
  const columns = useMemo(
    () => projects.filter((p): p is CompareColumn => Boolean(p.id)).slice(0, COMPARE_CART_MAX),
    [projects],
  );
  const [mode, setMode] = useState<CompareMode>('details');
  const availableModes = useMemo(
    () => MODES.filter((m) => m.id !== 'superRatio' || canUseSuperRatio),
    [canUseSuperRatio],
  );
  const superRatioMode = mode === 'superRatio' && canUseSuperRatio;

  // An emptied cart has nothing to chart — fall back to the details view.
  useEffect(() => {
    if (columns.length === 0) setMode('details');
  }, [columns.length]);

  // Never sit on a mode the viewer isn't granted.
  useEffect(() => {
    if (!canUseSuperRatio) setMode('details');
  }, [canUseSuperRatio]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'mr-popup-card',
        sx: {
          position: 'relative',
          borderRadius: '16px',
          overflow: 'hidden',
          color: '#eef6fa',
          background:
            'linear-gradient(160deg, rgba(10, 18, 32, 0.97) 0%, rgba(13, 27, 42, 0.97) 55%, rgba(9, 22, 34, 0.98) 100%)',
          backdropFilter: 'blur(14px)',
          border: '1px solid rgba(34, 211, 238, 0.3)',
          boxShadow:
            '0 24px 64px rgba(2, 8, 20, 0.6), 0 0 32px rgba(34, 211, 238, 0.12), inset 0 1px 0 rgba(255,255,255,0.06)',
          backgroundImage: 'none',
        },
      }}
    >
      <Box className="mr-scanline" />
      {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
        <Box
          key={corner}
          sx={{
            position: 'absolute',
            width: 14,
            height: 14,
            zIndex: 3,
            pointerEvents: 'none',
            borderColor: 'rgba(34, 211, 238, 0.75)',
            borderStyle: 'solid',
            borderWidth: 0,
            ...(corner === 'tl' && { top: 6, left: 6, borderTopWidth: 2, borderLeftWidth: 2 }),
            ...(corner === 'tr' && { top: 6, right: 6, borderTopWidth: 2, borderRightWidth: 2 }),
            ...(corner === 'bl' && { bottom: 6, left: 6, borderBottomWidth: 2, borderLeftWidth: 2 }),
            ...(corner === 'br' && { bottom: 6, right: 6, borderBottomWidth: 2, borderRightWidth: 2 }),
          }}
        />
      ))}

      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: 1.5,
          pt: 2,
          pb: 1,
          fontWeight: 800,
          fontSize: 18,
        }}
      >
        <ShoppingCartOutlinedIcon sx={{ color: CYAN, fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2, color: '#f1f7fb' }}>
            Compare Cart
          </Typography>
          <Typography sx={{ ...microLabelSx, color: CYAN, mt: 0.25 }}>
            Super Map · {columns.length}/{COMPARE_CART_MAX}
          </Typography>
        </Box>
        {columns.length > 0 && (
          <Button
            size="small"
            onClick={onClear}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: '#f87171',
              '&:hover': { bgcolor: 'rgba(248, 113, 113, 0.12)' },
            }}
          >
            Clear all
          </Button>
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(238, 246, 250, 0.7)' }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0.5, pb: 2.5, px: { xs: 1.5, sm: 3 } }}>
        {/* Without Super Ratio there is only one mode, so the switcher is noise. */}
        {columns.length > 0 && availableModes.length > 1 && (
          <Box
            sx={{
              display: 'inline-flex',
              gap: 0.5,
              mb: 1.5,
              p: 0.4,
              borderRadius: '10px',
              border: '1px solid rgba(34, 211, 238, 0.22)',
              bgcolor: 'rgba(8, 18, 28, 0.45)',
            }}
          >
            {availableModes.map((item) => {
              const active = mode === item.id;
              return (
                <Box
                  key={item.id}
                  component="button"
                  type="button"
                  onClick={() => setMode(item.id)}
                  sx={{
                    appearance: 'none',
                    cursor: 'pointer',
                    px: 1.25,
                    py: 0.55,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: active ? CYAN : 'transparent',
                    bgcolor: active ? 'rgba(34, 211, 238, 0.16)' : 'transparent',
                    color: active ? '#a5f3fc' : 'rgba(196, 220, 233, 0.7)',
                    fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
                    '&:hover': { bgcolor: active ? 'rgba(34, 211, 238, 0.22)' : 'rgba(34, 211, 238, 0.08)' },
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Box>
        )}

        {columns.length > 0 && superRatioMode ? (
          <CompareSuperRatioChart
            projects={columns}
            radiusMeters={radiusMeters}
            minRadiusMeters={minRadiusMeters}
          />
        ) : columns.length === 0 ? (
          <Box
            sx={{
              py: 5,
              textAlign: 'center',
              border: '1px dashed rgba(34, 211, 238, 0.25)',
              borderRadius: '12px',
              bgcolor: 'rgba(34, 211, 238, 0.04)',
            }}
          >
            <Typography sx={{ fontSize: 14, color: 'rgba(222, 238, 246, 0.75)' }}>
              No projects in the compare cart yet.
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(148, 197, 222, 0.65)', mt: 0.75 }}>
              Use Add to Compare on a project popup to add up to {COMPARE_CART_MAX}.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto', mx: { xs: -0.5, sm: 0 } }}>
            <Table
              size="small"
              sx={{
                minWidth: columns.length <= 2 ? '100%' : 560,
                width: '100%',
                tableLayout: 'fixed',
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      width: { xs: 78, sm: 118 },
                      borderColor: 'rgba(34, 211, 238, 0.18)',
                      color: 'rgba(148, 197, 222, 0.75)',
                      fontSize: { xs: 10, sm: 11 },
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      verticalAlign: 'bottom',
                      pb: 1.25,
                    }}
                  >
                    Overview
                  </TableCell>
                  {columns.map((project) => (
                    <TableCell
                      key={project.id}
                      sx={{
                        borderColor: 'rgba(34, 211, 238, 0.18)',
                        verticalAlign: 'top',
                        overflow: 'hidden',
                        pb: 1.25,
                      }}
                    >
                      <Box sx={{ position: 'relative', mb: 1 }}>
                        <Box
                          sx={{
                            width: '100%',
                            aspectRatio: '16 / 10',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            border: '1px solid rgba(34, 211, 238, 0.22)',
                            bgcolor: 'rgba(34, 211, 238, 0.06)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {project.featuredImage ? (
                            <Box
                              component="img"
                              src={project.featuredImage}
                              alt={project.name || 'Project'}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          ) : (
                            <ApartmentIcon sx={{ fontSize: 28, color: 'rgba(148, 197, 222, 0.45)' }} />
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => onRemove(project.id)}
                          aria-label={`Remove ${project.name || 'project'}`}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'rgba(8, 16, 28, 0.72)',
                            color: 'rgba(248, 113, 113, 0.95)',
                            '&:hover': { bgcolor: 'rgba(8, 16, 28, 0.9)' },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: { xs: 12.5, sm: 14 },
                          fontWeight: 800,
                          color: '#a5f3fc',
                          lineHeight: 1.25,
                          ...cellWrapSx,
                        }}
                      >
                        {project.name || 'Unnamed'}
                      </Typography>
                      {project.projectCategory && (
                        <Typography
                          sx={{
                            mt: 0.35,
                            fontSize: 10.5,
                            fontWeight: 600,
                            color: 'rgba(196, 220, 233, 0.7)',
                            ...cellWrapSx,
                          }}
                        >
                          {project.projectCategory}
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {SECTIONS.map((section) => (
                  <React.Fragment key={section.id}>
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + 1}
                        sx={{
                          borderColor: 'rgba(34, 211, 238, 0.18)',
                          bgcolor: 'rgba(34, 211, 238, 0.06)',
                          py: 0.85,
                          px: 1.25,
                        }}
                      >
                        <Typography sx={{ ...microLabelSx, color: CYAN }}>{section.title}</Typography>
                      </TableCell>
                    </TableRow>
                    {section.rows.map((row) => (
                      <TableRow key={`${section.id}-${row.key}`} hover>
                        <TableCell
                          sx={{
                            borderColor: 'rgba(34, 211, 238, 0.12)',
                            color: 'rgba(148, 197, 222, 0.75)',
                            fontSize: { xs: 10, sm: 11 },
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            verticalAlign: 'top',
                          }}
                        >
                          {row.label}
                        </TableCell>
                        {columns.map((project) => (
                          <TableCell
                            key={`${project.id}-${row.key}`}
                            sx={{
                              borderColor: 'rgba(34, 211, 238, 0.12)',
                              color: '#eef6fa',
                              fontSize: { xs: 12, sm: 13 },
                              fontWeight: 600,
                              verticalAlign: 'top',
                              ...cellWrapSx,
                            }}
                          >
                            {row.getValue(project)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CompareCartDialog;
